<template>
    <b-card title="Group Permission">
        <!-- search input -->
        <div class="custom-search d-flex justify-content-between">
            <b-form-group class="justify-content-end">
                <div class="d-flex align-items-center">
                    <label class="mr-1">Search</label>
                    <b-form-input
                        v-model="searchTerm"
                        placeholder="Search"
                        type="text"
                        class="d-inline-block"
                    />
                </div>
            </b-form-group>
            <b-form-group v-if="this.showAdd">
                <div class="d-flex align-items-center">
                    <b-button
                        id="toggle-btn"
                        v-b-modal.modal-prevent-closing
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        variant="outline-primary"
                        @click="addNew()"
                    >
                        <feather-icon
                            icon="PlusIcon"
                            class="mr-50"
                        />
                        <span class="align-middle">Add New Group</span>
                    </b-button>
                </div>
            </b-form-group>
        </div>
    
        <!-- table -->
        <vue-good-table
            :columns="columns"
            :rows="rows"
            :rtl="direction"
            :search-options="{
                enabled: true,
                externalQuery: searchTerm 
            }"
            :pagination-options="{
                enabled: true,
                perPage:pageLength
            }"
            styleClass="vgt-table striped condensed no-outline"
        >
    
            <template
                slot="table-row"
                slot-scope="props"
            >
    
                <!-- Column: Name -->
                <span
                    v-if="props.column.field === 'name'"
                    class="text-nowrap"
                >
                    <span class="text-nowrap">{{ props.row.name }}</span>
                </span>

                <!-- Column: Group Permissions -->
                <span
                 v-else-if="props.column.field === 'group_permissions'"
                    class="text-nowrap"
                >
                    <router-link :to="{ path: '/admin/settings/group-permission/' + props.row.id }">
                        <span class="btn btn-secondary">View Permissions</span>
                    </router-link>
                </span>

                <!-- Column: Status -->
                <span v-else-if="props.column.field === 'index'">
                    <span>{{props.row.originalIndex+1}}</span>
                </span>

                <!-- Column: Action -->
                <span v-else-if="props.column.field === 'action'">
                    <span>
                        <b-button 
                            @click="assignUser(props.row)"
                            v-b-modal.modal-assign-user
                            id="assign-modal"
                            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                            variant="primary" class="btn-icon rounded-circle mr-1"
                        >
                            <feather-icon
                                icon="UserIcon"
                            />
                        </b-button>
                        <b-button @click="deleteType(props.row)"
                            id="delete-btn"
                            v-b-modal.modal-delete
                            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                            variant="primary" class="btn-icon rounded-circle"
                            v-if="showDelete"
                        >
                            <feather-icon
                                icon="TrashIcon"
                            />
                        </b-button>
                    </span>
                </span>
        
                <!-- Column: Common -->
                <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                </span>
            </template>
    
            <!-- pagination -->
            <template
                slot="pagination-bottom"
                slot-scope="props"
            >
                <div class="d-flex justify-content-between flex-wrap">
                    <div class="d-flex align-items-center mb-0 mt-1">
                        <span class="text-nowrap ">
                            Showing 1 to
                        </span>
                        <b-form-select
                            v-model="pageLength"
                            :options="['3','5','10','50','100']"
                            class="mx-1"
                            @input="(value)=>props.perPageChanged({currentPerPage:value})"
                        />
                        <span class="text-nowrap"> of {{ props.total }} entries </span>
                    </div>
                    <div>
                        <b-pagination
                            :value="1"
                            :total-rows="props.total"
                            :per-page="pageLength"
                            first-number
                            last-number
                            align="right"
                            prev-class="prev-item"
                            next-class="next-item"
                            class="mt-1 mb-0"
                            @input="(value)=>props.pageChanged({currentPage:value})"
                        >
                            <template #prev-text>
                                <feather-icon
                                    icon="ChevronLeftIcon"
                                    size="18"
                                />
                            </template>
                            <template #next-text>
                                <feather-icon
                                    icon="ChevronRightIcon"
                                    size="18"
                                />
                            </template>
                        </b-pagination>
                    </div>
                </div>
            </template>
        </vue-good-table>
    
        <!-- Add New Group -->
        <b-modal
            id="modal-prevent-closing"
            ref="my-modal"
            centered
            title="Add New Group"
            ok-title="Submit"
            cancel-variant="outline-secondary"
            @show="resetModal"
            @hidden="resetModal"
            @ok="handleOk"
        >
            <form
                ref="form"
                @submit.stop.prevent="handleSubmit"
            >
                <b-form-group
                    :state="nameState"
                    label="Group Name"
                    label-for="name-input"
                    invalid-feedback="Group name is required"
                >
                    <b-form-input
                        id="name-input"
                        v-model="modify.name"
                        :state="nameState"
                        required
                    />
                </b-form-group>
            </form>
        </b-modal>

        <!-- Assign User to Group --> 
        <b-modal
            id="modal-assign-user"
            ref="assign-modal"
            centered
            title="Assign Admin to Group"
            ok-title="Submit"
            cancel-variant="outline-secondary"
            @show="resetModal"
            @hidden="resetModal"
            @ok="handleAssignOk"
        >
            <form
                ref="form"
                @submit.stop.prevent="handleSubmit"
            >
                <b-form-group
                    :state="nameState"
                    label="Admin"
                    label-for="name-input"
                    invalid-feedback="Admin is required"
                >
                    <b-form-select v-model="user.profile_id" :options="formattedOptions"></b-form-select>
                </b-form-group>
            </form>

            <span style="display: flex; flex-direction: column;">
                <span style="margin: 8px 0px; font-weight: 600;">Already Assigned:</span>
                <span v-for="user, index in admins_by_group" :key="index">
                    <div>
                        <span style="display: flex; flex-direction: row;">
                            <span style="width: 250px; padding: 8px;">{{ user.first_name }} {{ user.last_name }}</span>
                            <span v-b-modal.modal-member-delete id="member-btn" @click="deleteAssignedUser(user.id)" style="padding: 8px; cursor: pointer;">X</span>
                        </span>
                    </div>
                </span>
            </span>
        </b-modal>

        <!-- Delete Group -->
        <b-modal
            id="modal-delete"
            ref="modal-delete"
            centered
            modal-class="modal-danger"
            title="Are you sure?"
        >
            <b-card-text class="border-2">
                <span>If you delete, you won't be able to revert this action!!</span>
            </b-card-text>
    
            <template #modal-footer>
                <div class="w-100">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="danger"
                        class="float-right"
                        @click="confirmDelete()"
                    >
                        Accept
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="float-right mr-1"
                        @click="$refs['modal-delete'].toggle('#delete-btn')"
                    >
                        Cancel
                    </b-button>
                </div>
            </template>
        </b-modal>

        <!-- Delete Assigned Admin -->
        <b-modal
            id="modal-member-delete"
            ref="member-delete"
            centered
            modal-class="modal-danger"
            title="Are you sure?"
        >
            <b-card-text class="border-2">
                <span>If you delete, you won't be able to revert this action!!</span>
            </b-card-text>
    
            <template #modal-footer>
                <div class="w-100">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="danger"
                        class="float-right"
                        @click="confirmMemberDelete()"
                    >
                        Accept
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="float-right mr-1"
                        @click="$refs['member-delete'].toggle('#member-btn')"
                    >
                        Cancel
                    </b-button>
                </div>
            </template>
        </b-modal>
    </b-card>
</template>
  
<script>
import {
    BAvatar,
    BButton,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BCard,
    BModal,
    VBModal,
    BCardText,
    BFormFile
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import vSelect from "vue-select"
import Ripple from "vue-ripple-directive";
import {mapState} from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
  
export default {
    components: {
        VueGoodTable,
        BAvatar,
        BCard,
        BBadge,
        BButton,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BDropdown,
        BDropdownItem,
        BModal,
        BCardText,
        BFormFile,
        vSelect
    },

    directives: {
        'b-modal': VBModal,
        Ripple,
    },

    computed: {
        ...mapState('permission', ["group","groupError","groupLoading", "submitError","submitSuccess","submitLoading"]),
        ...mapState('admin', ["admins"]),

        formattedOptions() {
            return this.admins.map(user => ({
                value: user.id, 
                text: `${user.first_name} ${user.last_name}`
            }));
        },
    
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },

    watch: {
        group(newValue, oldValue) {
            if (newValue) {
                this.rows = this.group
            }
        }
    },

    data() {
        return {
            host: "https://api-biyeruti.smicee.com",
            nameState: null,
            addNewMode: false,
            popupActive: true,
            modify: {
                name: null
            },
            pageLength: 10,
            dir: false,
            columns: [
                {
                    label: '#',
                    field: 'index',
                    type:'number',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Group',
                    field: 'name',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Permissions',
                    field: 'group_permissions',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
            ],
            user: {
                profile_id: null,
                group_name: null
            },
            admin_id: null,
            admins_by_group: null,
            delete_id: null,
            showAdd: true,
            showEdit: true,
            showDelete: true,
            rows: [],
            searchTerm: ''
        }
    },

    created() {
        this.$store.dispatch('admin/fetchAdminList')
        this.$store.dispatch('permission/fetchUserGroup')
        this.rows = this.group
        let permissions=JSON.parse(localStorage.getItem('permissions'))
        // this.showAdd=permissions.includes("add_state")
        // this.showEdit=permissions.includes("change_state")
        // this.showDelete=permissions.includes("delete_state")
        if(this.showEdit||this.showDelete){
            this.columns.push( {
            label: 'Action',
            field: 'action',
            tdClass: 'text-center',
            thClass:'text-center',
            sortable: false,
            })
        }
    },

    methods: {
        checkFormValidity() {
            const valid = this.$refs.form.checkValidity()
            this.nameState = valid
            return valid
        },

        resetModal() {
            this.nameState = null
        },

        handleOk(bvModalEvt) {
            bvModalEvt.preventDefault()
            this.handleSubmit()
        },

        handleAssignOk(bvModalEvt) {
            bvModalEvt.preventDefault()
            this.handleAssignUser()
        },

        addNew() {
            this.modify.name = null
            this.addNewMode = true;
        },

        assignUser(row) {
            this.user.group_name = row.name
            this.admins_by_group = row.users
        },

        async handleSubmit() {
            if (!this.checkFormValidity()) {
                return
            }
            if(this.addNewMode){
                await this.$store.dispatch('permission/addUserGroup', this.modify)
                .then(result=>{
                    if(result.code == 200) {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'ThumbsUpIcon',
                                variant: 'success',
                                text: result.response
                            }
                        })
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Error`,
                                icon: 'ThumbsDownIcon',
                                variant: 'error',
                                text: result.response
                            }
                        })
                    }
                })
            } 
            // else {
            //     await this.$store.dispatch('country/updateCountry',this.modify).then(result=> {
            //         this.$toast({
            //             component: ToastificationContent,
            //             position: 'top-right',
            //             props: {
            //                 title: `Success`,
            //                 icon: 'ThumbsUpIcon',
            //                 variant: 'success',
            //                 text: result,
            //             },
            //         })
            //     })
            // }
            this.$nextTick(() => {
                this.$refs['my-modal'].toggle('#toggle-btn')
            })
        },

        async handleAssignUser() {
            await this.$store.dispatch('permission/assignUsertoGroup', this.user)
            .then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: result.response
                        }
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Error`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.response
                        }
                    })
                }
            })
            this.$nextTick(() => {
                this.$refs['assign-modal'].toggle('#toggle-btn')
            })

            this.user.profile_id = null
            this.user.group_name = null
        },

        initValues(){
            // this.modify = {
            //     id: null,
            //     name: null,
            // }
        },

        deleteType(data) {
            this.delete_id = data.id
        },

        async confirmDelete(){
            await this.$store.dispatch("permission/deleteGroup", this.delete_id).then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: result.response
                        }
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Error`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.response
                        }
                    })
                }
                this.delete_id = null
            })
            this.$nextTick(() => {
                this.$refs['modal-delete'].toggle('#delete-btn')
            })
        },

        async deleteAssignedUser(user_id){
            this.admin_id = user_id
        },

        async confirmMemberDelete(){
            await this.$store.dispatch("permission/deleteAssignedAdmin", {
                profile_id: this.admin_id,
                group_name: this.user.group_name 
            }).then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: result.response
                        }
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Error`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.response
                        }
                    })
                }
                
                this.admin_id = null
            })
            this.$nextTick(() => {
                this.$refs['member-delete'].toggle('#member-btn')
            })
            this.$nextTick(() => {
                this.$refs['assign-modal'].toggle('#toggle-btn')
            })
        }
    }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

@import '@core/scss/vue/libs/vue-good-table.scss';
</style>